/**
 * AdminTab Component
 *
 * Provides quick access to admin tools with badge indicators for pending items.
 * Only visible to admin users on the Profile page.
 *
 * @example
 * ```tsx
 * <AdminTab pendingGymRequests={3} pendingFeedback={5} />
 * ```
 */

import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ImageIcon from '@mui/icons-material/Image';

/**
 * Props for AdminTab component
 */
interface AdminTabProps {
  /** Number of pending gym registration requests */
  pendingGymRequests: number;
  /** Number of pending feedback items */
  pendingFeedback: number;
}

/**
 * AdminTab displays quick access links to admin tools with pending count badges
 */
export function AdminTab({ pendingGymRequests, pendingFeedback }: AdminTabProps) {
  return (
    <div style={containerStyle}>
      <h3 style={sectionTitleStyle}>Admin Tools</h3>

      <Link to="/admin" style={toolCardStyle}>
        <DashboardIcon sx={iconStyle} />
        <span style={toolNameStyle}>Dashboard</span>
      </Link>

      <Link to="/admin/users" style={toolCardStyle}>
        <PeopleIcon sx={iconStyle} />
        <span style={toolNameStyle}>Manage Users</span>
      </Link>

      <Link to="/admin/gym-requests" style={toolCardStyle}>
        <BusinessIcon sx={iconStyle} />
        <span style={toolNameStyle}>Gym Requests</span>
        {pendingGymRequests > 0 && <span style={badgeStyle}>{pendingGymRequests}</span>}
      </Link>

      <Link to="/admin/feedback" style={toolCardStyle}>
        <FeedbackIcon sx={iconStyle} />
        <span style={toolNameStyle}>Feedback</span>
        {pendingFeedback > 0 && <span style={badgeStyle}>{pendingFeedback}</span>}
      </Link>

      <Link to="/admin/flyers" style={toolCardStyle}>
        <ImageIcon sx={iconStyle} />
        <span style={toolNameStyle}>Flyer Generator</span>
      </Link>
    </div>
  );
}

const containerStyle: CSSProperties = {
  padding: 'var(--spacing-md)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  marginBottom: 'var(--spacing-sm)',
};

const toolCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  marginBottom: 'var(--spacing-sm)',
  textDecoration: 'none',
  color: 'inherit',
};

const iconStyle = {
  color: 'var(--color-text-secondary)',
};

const toolNameStyle: CSSProperties = {
  flex: 1,
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
};

const badgeStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 'var(--radius-full)',
};

export default AdminTab;
